// 30/60/90 day PPM dashboard (/office/ppm, Session AJ Item 11). Every lift grouped by when its
// next service falls: Overdue, 0 to 30 days (red), 31 to 60 (amber), 61 to 90 (green), Beyond 90
// (grey, collapsed) and a No-contract bucket, plus (Session BDH, D-BDH-7) a Not assessed band for a lift with no
// recorded service that would otherwise read overdue.
// It reads GET /api/assets, which returns the
// LIVE next-service projection (nextScheduledServiceAt + serviceOverdue + nextServiceTone) computed
// by enrichAssetStatus, never the stale stored Asset.nextServiceDue. Clicking a row opens the lift.
// Session SC2D (scale run): the SERVER works out each lift's band (scheduling/ppmBands.ts, held equal to src/lib/ppmBands.ts by a
// parity test). The page asks for the count of every band (ppmCounts) and then one PAGE of each open band (ppmBand, limit, offset), so
// a thousand lifts is never downloaded to be grouped in the browser.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { assets } from "../../lib/resources";
import type { Asset } from "../../lib/resources";
import { PPM_BANDS, daysUntilNextService, type PpmBandKey, type PpmBandMeta } from "../../lib/ppmBands";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { valueToLabel } from "./register/RegisterPage";
import { FREQUENCY } from "./register/AssetsPage";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Loader } from "../../components/Loader";
import { toneTextClass } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

// "Beyond 90 days" and "No contract or not scheduled" carry no urgency, so they are collapsed by
// default to keep the focus on what needs attention.
const COLLAPSED_BY_DEFAULT: PpmBandKey[] = ["beyond", "none"];

function daysLabel(a: Asset, now: Date, band?: PpmBandKey): string {
  // Session BDH, D-BDH-7: a lift with no recorded service is not assessed, so it never reads as days overdue.
  if (band === "not_assessed") return "Not assessed";
  const d = daysUntilNextService(a, now);
  if (d === null) return "Not scheduled";
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} overdue`;
  if (d === 0) return "Due today";
  return `In ${d} day${d === 1 ? "" : "s"}`;
}

const PAGE_SIZE = 50;

type BandCounts = Record<PpmBandKey, number>;
interface BandPage {
  items: Asset[];
  total: number;
}

export function PpmDashboardPage() {
  const [counts, setCounts] = useState<BandCounts | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // Established mobile-card mechanism (Session AHB): the layout is decided ONCE at mount, and exactly one of the card list or the table
  // renders, never both. The shared MobileListCard's primary/secondary never truncate. jsdom has no matchMedia, so every existing test
  // still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let active = true;
    setState("loading");
    void (async () => {
      try {
        const data = (await assets.list({ ppmCounts: "true" })) as unknown as { counts: BandCounts };
        if (active) {
          setCounts(data.counts);
          setState("ready");
        }
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  function isOpen(key: PpmBandKey): boolean {
    return open[key] ?? !COLLAPSED_BY_DEFAULT.includes(key);
  }

  return (
    <div>
      {/* Session ALU: renamed with the nav item ("Service schedule (PPM)" collided with "Scheduling", the forward calendar). The initials
          the industry actually says live in the standfirst. */}
      <PageHeader
        className="mb-4"
        title="Due for service"
        subtitle="Every lift grouped by when its next service falls due, from the live planned preventative maintenance (PPM) schedule. Click a lift to open its record."
      />

      {state === "loading" ? (
        <Loader />
      ) : state === "error" || !counts ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the lifts. Please try again in a moment.
        </p>
      ) : (
        <div className="space-y-5">
          {PPM_BANDS.map((band) => (
            <BandSection
              key={band.key}
              band={band}
              count={counts[band.key] ?? 0}
              expanded={isOpen(band.key)}
              onToggle={() => setOpen((o) => ({ ...o, [band.key]: !isOpen(band.key) }))}
              mobileCards={mobileCards}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// One band: its header (always, with the server's count) and, while it is open and not empty, ONE PAGE of its lifts.
function BandSection({ band, count, expanded, onToggle, mobileCards }: { band: PpmBandMeta; count: number; expanded: boolean; onToggle: () => void; mobileCards: boolean }) {
  const navigate = useNavigate();
  const page = useServerPage<BandPage>(
    band.key,
    (offset) => assets.listPage!({ ppmBand: band.key, limit: String(PAGE_SIZE), offset: String(offset) }) as Promise<BandPage>,
    expanded && count > 0,
  );
  const rows = page.data?.items ?? [];
  // A single `now` per answer so the day labels agree with the server's banding.
  const now = useMemo(() => new Date(), [page.data]);

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between px-4 py-3 text-left" aria-expanded={expanded}>
        <span className={`font-heading text-lg ${toneTextClass(band.tone)}`}>
          {band.label} <span className="text-ele-body">({count})</span>
        </span>
        <span aria-hidden className="text-ele-body">{expanded ? "−" : "+"}</span>
      </button>
      {expanded &&
        (count === 0 ? (
          <p className="px-4 pb-4 text-sm text-ele-body opacity-70">No lifts in this band.</p>
        ) : page.loading ? (
          <div className="px-4 pb-4"><Loader /></div>
        ) : page.error && !page.data ? (
          <p role="alert" className="px-4 pb-4 text-sm text-ele-strong">Could not load this band. Please try again in a moment.</p>
        ) : (
          <div className={page.refreshing ? "opacity-60" : undefined}>
            {mobileCards ? (
              /* Session AHB pattern: below md each lift is a full-width tappable card (ELE number, site, next due + urgency) instead of a
                 squeezed table row. primary and secondary never truncate (MobileListCard). */
              <div className="border-t border-ele-purple-accent">
                <MobileCardList label={band.label}>
                  {rows.map((a) => (
                    <MobileListCard
                      key={a.id}
                      to={`/office/lifts/${a.id}`}
                      primary={<span className="font-heading text-ele-green">{a.eleNumber ?? "None"}</span>}
                      secondary={a.site?.name ?? "None"}
                      meta={(a.nextScheduledServiceAt ? formatDate(a.nextScheduledServiceAt) : "Not scheduled")}
                      right={<span className={`text-sm font-semibold ${toneTextClass(band.tone)}`}>{daysLabel(a, now, band.key)}</span>}
                    />
                  ))}
                </MobileCardList>
              </div>
            ) : (
              <div className="overflow-hidden border-t border-ele-purple-accent">
                {/* Session ACW Loop 6: Lift ID / Site / Next due are the priority columns below md; Frequency and Last serviced fold away.
                    Every row navigates to the lift on click with no other interactive element inside it; a mobile-only chevron is added purely
                    as a visual affordance. The base col widths are the pre-Loop-6 desktop values, unchanged. */}
                <table className="ele-list-table w-full text-left text-sm">
                  <colgroup>
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "16%" }} />
                    <col className="max-md:hidden" style={{ width: "15%" }} />
                    <col className="max-md:hidden" style={{ width: "14%" }} />
                    <col className="max-md:hidden" style={{ width: "14%" }} />
                    <col className="w-8 md:hidden" />
                  </colgroup>
                  <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                    <tr>
                      <th className="px-3 py-2">Lift ID</th>
                      <th className="px-3 py-2">Site</th>
                      <th className="px-3 py-2">Next due</th>
                      <th className="max-md:hidden px-3 py-2">Days</th>
                      <th className="max-md:hidden px-3 py-2">Frequency</th>
                      <th className="max-md:hidden px-3 py-2">Last serviced</th>
                      <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr
                        key={a.id}
                        onClick={() => navigate(`/office/lifts/${a.id}`)}
                        className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                      >
                        <td className="px-3 py-2 align-top font-heading text-ele-green">{a.eleNumber ?? "None"}</td>
                        <td className="px-3 py-2 align-top">{a.site?.name ?? "None"}</td>
                        <td className="px-3 py-2 align-top">{(a.nextScheduledServiceAt ? formatDate(a.nextScheduledServiceAt) : "Not scheduled")}</td>
                        <td className="max-md:hidden px-3 py-2 align-top">{daysLabel(a, now, band.key)}</td>
                        <td className="max-md:hidden px-3 py-2 align-top">{valueToLabel(FREQUENCY, a.frequency)}</td>
                        <td className="max-md:hidden px-3 py-2 align-top">{(a.lastServicedAt ? formatDate(a.lastServicedAt) : "Not recorded")}</td>
                        <td className="cell-actions md:hidden pr-1 text-right">
                          <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green">
                            <path d="M6 4l4 4-4 4z" />
                          </svg>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-4 pb-3">
              <Pager offset={page.offset} total={page.data?.total ?? count} shown={rows.length} pageSize={PAGE_SIZE} onOffset={page.setOffset} noun={["lift", "lifts"]} />
            </div>
          </div>
        ))}
    </section>
  );
}
